import { configureStore } from '@reduxjs/toolkit';
import { botsApi } from './bots/botsApi';
import { tagsApi } from './tags/tagsApi';
import { adminApi } from './admin/adminApi';
import chatReducer, { attachChatListeners } from './chat/chatSlice';
import uiReducer, { uiActions } from './ui/uiSlice';
import authReducer from './auth/authSlice';
import clinicsReducer from './clinics/clinicsSlice';

// Create the Redux store
export function createAppStore() {
  const store = configureStore({
    reducer: {
      // Feature slices
      chat: chatReducer,
      ui: uiReducer,
      auth: authReducer,
      clinics: clinicsReducer,
      // RTK Query APIs
      [botsApi.reducerPath]: botsApi.reducer,
      [tagsApi.reducerPath]: tagsApi.reducer,
      [adminApi.reducerPath]: adminApi.reducer,
    },
    middleware: (getDefault) =>
      getDefault({
        // We keep Redux state serializable; chatService keeps non-serializables internally
        serializableCheck: {
          ignoredActionPaths: ['meta.arg', 'payload.config', 'payload.request', 'error'],
          ignoredPaths: [
            // Ignore RTK Query internals
            `${botsApi.reducerPath}`,
            `${tagsApi.reducerPath}`,
            `${adminApi.reducerPath}`,
          ],
        },
      }).concat(botsApi.middleware, tagsApi.middleware, adminApi.middleware),
    devTools: (typeof import.meta !== 'undefined' && (import.meta as any).env
      ? (import.meta as any).env.MODE !== 'production'
      : true),
  });

  // Side effects that must be attached once per store instance
  attachChatListeners(store);
  attachUISideEffects(store);
  registerBotsBroadcastInvalidation(store);
  registerStorageFallbackInvalidation(store);
  registerTagsBroadcastInvalidation(store);
  registerTagsStorageFallbackInvalidation(store);

  return store;
}

// Single store instance for the app
export const store = createAppStore();

// Typed helpers
export type AppStore = typeof store;
export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;

// UI side-effects: close mobile drawer on Escape
function attachUISideEffects(store: AppStore) {
  const onKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      store.dispatch(uiActions.closeMobile());
    }
  };
  window.addEventListener('keydown', onKeyDown);
  // No cleanup here; store lives for lifetime of app
}

// Cross-tab invalidation for Bots via BroadcastChannel
function registerBotsBroadcastInvalidation(store: AppStore) {
  try {
    const bc = new BroadcastChannel('chat-bots');
    bc.onmessage = (ev: MessageEvent) => {
      const data = (ev?.data || {}) as any;
      if (data?.type === 'bots-changed') {
        invalidateBots(store);
      }
    };
  } catch {
    // Ignore if BroadcastChannel is not supported
  }
}

// Storage-event fallback invalidation for Bots
function registerStorageFallbackInvalidation(store: AppStore) {
  const onStorage = (e: StorageEvent) => {
    if (e.key === 'chatBots:ping' && e.newValue) {
      invalidateBots(store);
    }
  };
  window.addEventListener('storage', onStorage);
}

function invalidateBots(store: AppStore) {
  // Invalidate all relevant bot-related caches
  store.dispatch(botsApi.util.invalidateTags(['Bots', 'ChannelsStatus', 'ActiveByChannel', 'Plans', 'Usage']));
}

// Cross-tab invalidation for Tags via BroadcastChannel
function registerTagsBroadcastInvalidation(store: AppStore) {
  try {
    const bc = new BroadcastChannel('chat-tags');
    bc.onmessage = (ev: MessageEvent) => {
      const data = (ev?.data || {}) as any;
      if (data?.type === 'tags-changed') {
        invalidateTags(store);
      }
    };
  } catch {
    // Ignore if BroadcastChannel is not supported
  }
}

// Storage-event fallback invalidation for Tags
function registerTagsStorageFallbackInvalidation(store: AppStore) {
  const onStorage = (e: StorageEvent) => {
    if (e.key === 'Tags:ping' && e.newValue) {
      invalidateTags(store);
    }
  };
  window.addEventListener('storage', onStorage);
}

function invalidateTags(store: AppStore) {
  // Invalidate tag and folder caches (thread tags are invalidated via SSE in chatSlice)
  store.dispatch(tagsApi.util.invalidateTags(['Tags', 'Folders']));
}
